<template>
        <div class="min-h-screen bg-gray-50 font-sans">
                <!-- Header -->
                <header class="bg-white border-b border-gray-200 py-4 px-3">
                        <div class="w-full flex justify-between items-center flex-wrap gap-2">
                                <div>
                                        <h1 class="text-2xl font-semibold text-gray-900 m-0">API Permissions</h1>
                                        <p class="text-sm text-gray-500 mt-1 mb-0">Manage API application permissions</p>
                                </div>
                                <button
                                        type="button"
                                        class="inline-flex items-center gap-2 bg-orange-7 hover:bg-orange-6 text-white py-2.5 px-4 rounded-lg text-sm font-medium border-none cursor-pointer transition-colors"
                                        @click="openCreateModal"
                                >
                                        <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                                <path stroke-linecap="round" stroke-linejoin="round" d="M12 4v16m8-8H4" />
                                        </svg>
                                        Create New
                                </button>
                        </div>
                </header>

                <!-- Main Content -->
                <main class="w-full p-3">
                        <!-- Card -->
                        <div class="bg-white rounded-xl shadow-sm overflow-hidden">
                                <!-- Card Header -->
                                <div class="py-4 px-6 border-b border-gray-200 flex flex-wrap justify-between items-center gap-4">
                                        <div class="flex items-center gap-3">
                                                <h2 class="text-lg font-semibold text-gray-900 m-0">Applications</h2>
                                                <span class="inline-flex items-center py-1 px-2.5 text-xs font-medium bg-orange-2 text-orange-7 rounded-full">
                                                        {{ totalRows }} total
                                                </span>
                                        </div>
                                        <div class="flex items-center gap-3">
                                                <!-- Search -->
                                                <div class="relative">
                                                        <svg class="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                                                <path stroke-linecap="round" stroke-linejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                                        </svg>
                                                        <input
                                                                v-model="searchQuery"
                                                                type="text"
                                                                placeholder="Search by partner or IP..."
                                                                class="py-2 px-3 pl-9 border border-gray-300 rounded-lg text-sm w-64 outline-none transition-all focus:border-orange-7 focus:ring-2 focus:ring-orange-2"
                                                        >
                                                </div>
                                                <!-- Refresh Button -->
                                                <button
                                                        type="button"
                                                        class="inline-flex items-center gap-2 py-2 px-4 border border-gray-300 bg-white hover:bg-gray-50 rounded-lg text-sm font-medium text-gray-700 cursor-pointer transition-colors"
                                                        @click="refreshData"
                                                >
                                                        <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                                                <path stroke-linecap="round" stroke-linejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                                        </svg>
                                                        Refresh
                                                </button>
                                        </div>
                                </div>

                                <!-- Table -->
                                <div class="overflow-x-auto">
                                        <!-- Loading State -->
                                        <div v-if="loading" class="flex justify-center items-center py-16 px-4">
                                                <svg class="w-10 h-10 text-orange-7 animate-spin" fill="none" viewBox="0 0 24 24">
                                                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                                                        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                </svg>
                                                <span class="ml-3 text-gray-500 text-sm">Loading applications...</span>
                                        </div>

                                        <!-- Empty State -->
                                        <div v-else-if="filteredApplications.length === 0" class="text-center py-16 px-4">
                                                <svg class="w-12 h-12 text-gray-300 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5">
                                                        <path stroke-linecap="round" stroke-linejoin="round" d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                                                </svg>
                                                <h3 class="text-base font-medium text-gray-700 m-0 mb-1">No applications</h3>
                                                <p class="text-sm text-gray-500 m-0">Get started by creating a new application.</p>
                                        </div>

                                        <!-- Data Table -->
                                        <table v-else class="w-full border-collapse">
                                                <thead>
                                                        <tr class="bg-gray-50">
                                                                <th class="py-3 px-6 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">ID</th>
                                                                <th class="py-3 px-6 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Partner</th>
                                                                <th class="py-3 px-6 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">IP Address</th>
                                                                <th class="py-3 px-6 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Actions</th>
                                                        </tr>
                                                </thead>
                                                <tbody>
                                                        <tr
                                                                v-for="app in paginatedApplications"
                                                                :key="app.id"
                                                                class="border-t border-gray-200 transition-colors hover:bg-gray-50"
                                                        >
                                                                <td class="py-4 px-6 text-sm text-gray-500">{{ app.id }}</td>
                                                                <td class="py-4 px-6 text-sm font-medium text-gray-900">{{ app.partner || '-' }}</td>
                                                                <td class="py-4 px-6 text-sm text-gray-500 font-mono">{{ app.ipaddress || '-' }}</td>
                                                                <td class="py-4 px-6 text-right">
                                                                        <div class="inline-flex items-center gap-2">
                                                                                <button
                                                                                        type="button"
                                                                                        class="inline-flex items-center justify-center w-8 h-8 rounded-md bg-transparent border-none text-gray-500 cursor-pointer transition-colors hover:bg-orange-2 hover:text-orange-7"
                                                                                        title="Edit Routes"
                                                                                        @click="editApplication(app)"
                                                                                >
                                                                                        <svg class="w-4.5 h-4.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                                                                                <path stroke-linecap="round" stroke-linejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                                                                        </svg>
                                                                                </button>
                                                                                <button
                                                                                        type="button"
                                                                                        class="inline-flex items-center justify-center w-8 h-8 rounded-md bg-transparent border-none text-gray-500 cursor-pointer transition-colors hover:bg-red-50 hover:text-red-600"
                                                                                        title="Delete"
                                                                                        @click="confirmDeleteApplication(app)"
                                                                                >
                                                                                        <svg class="w-4.5 h-4.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                                                                                <path stroke-linecap="round" stroke-linejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                                                        </svg>
                                                                                </button>
                                                                        </div>
                                                                </td>
                                                        </tr>
                                                </tbody>
                                        </table>
                                </div>

                                <!-- Pagination -->
                                <div v-if="filteredApplications.length > 0" class="py-3 px-6 border-t border-gray-200 flex flex-wrap justify-between items-center gap-4 bg-gray-50">
                                        <div class="flex items-center gap-2 text-sm text-gray-500">
                                                <span>Showing {{ startRow }} to {{ endRow }} of {{ totalRows }} results</span>
                                        </div>
                                        <div class="flex items-center gap-2">
                                                <select
                                                        v-model="pageSize"
                                                        class="py-1.5 px-3 pr-8 border border-gray-300 rounded-md text-sm bg-white cursor-pointer outline-none"
                                                >
                                                        <option :value="10">10 per page</option>
                                                        <option :value="25">25 per page</option>
                                                        <option :value="50">50 per page</option>
                                                        <option :value="100">100 per page</option>
                                                </select>
                                                <div class="flex items-center gap-1">
                                                        <button
                                                                type="button"
                                                                :disabled="currentPage === 1"
                                                                class="inline-flex items-center justify-center w-8 h-8 border border-gray-300 rounded-md bg-white cursor-pointer transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                                                @click="goToPage(currentPage - 1)"
                                                        >
                                                                <svg class="w-4 h-4 text-gray-700" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                                                        <path stroke-linecap="round" stroke-linejoin="round" d="M15 19l-7-7 7-7" />
                                                                </svg>
                                                        </button>
                                                        <span class="px-3 text-sm text-gray-700">{{ currentPage }} / {{ totalPages }}</span>
                                                        <button
                                                                type="button"
                                                                :disabled="currentPage === totalPages"
                                                                class="inline-flex items-center justify-center w-8 h-8 border border-gray-300 rounded-md bg-white cursor-pointer transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                                                @click="goToPage(currentPage + 1)"
                                                        >
                                                                <svg class="w-4 h-4 text-gray-700" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                                                        <path stroke-linecap="round" stroke-linejoin="round" d="M9 5l7 7-7 7" />
                                                                </svg>
                                                        </button>
                                                </div>
                                        </div>
                                </div>
                        </div>
                </main>

                <!-- Create Modal -->
                <div v-if="showCreateModal" class="fixed inset-0 z-50">
                        <div class="fixed inset-0 bg-black/50" @click="closeCreateModal"></div>
                        <div class="fixed inset-0 overflow-y-auto">
                                <div class="flex min-h-full items-center justify-center p-4">
                                        <div class="w-full max-w-md overflow-hidden rounded-2xl bg-white p-6 shadow-2xl relative z-[51]">
                                                <h3 class="text-xl font-semibold text-gray-900 m-0">
                                                        Create New Application
                                                </h3>
                                                <p class="mt-1 text-sm text-gray-500">
                                                        Add a new API application.
                                                </p>
                                                <div class="mt-6">
                                                        <label for="app-partner" class="block text-sm font-medium text-gray-700">
                                                                Partner
                                                        </label>
                                                        <input
                                                                id="app-partner"
                                                                v-model="newAppPartner"
                                                                type="text"
                                                                placeholder="Enter partner name"
                                                                class="mt-1 block w-full py-2.5 px-3.5 border border-gray-300 rounded-lg text-sm outline-none transition-all box-border focus:border-orange-7 focus:ring-2 focus:ring-orange-2"
                                                        >
                                                </div>
                                                <div class="mt-4">
                                                        <label for="app-ipaddress" class="block text-sm font-medium text-gray-700">
                                                                IP Address
                                                        </label>
                                                        <input
                                                                id="app-ipaddress"
                                                                v-model="newAppIpaddress"
                                                                type="text"
                                                                placeholder="Enter IP address (e.g., 192.168.1.1)"
                                                                class="mt-1 block w-full py-2.5 px-3.5 border border-gray-300 rounded-lg text-sm outline-none transition-all box-border font-mono focus:border-orange-7 focus:ring-2 focus:ring-orange-2"
                                                        >
                                                </div>
                                                <div class="mt-6 flex flex-col gap-3">
                                                        <button
                                                                type="button"
                                                                :disabled="!newAppPartner.trim() || saving"
                                                                class="w-full py-2.5 px-4 text-sm font-medium text-white bg-orange-7 hover:bg-orange-6 border-none rounded-lg cursor-pointer transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                                                @click="createApplication"
                                                        >
                                                                {{ saving ? 'Creating...' : 'Create Application' }}
                                                        </button>
                                                        <button
                                                                type="button"
                                                                class="w-full py-2.5 px-4 text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 border border-gray-300 rounded-lg cursor-pointer transition-colors"
                                                                @click="closeCreateModal"
                                                        >
                                                                Cancel
                                                        </button>
                                                </div>
                                        </div>
                                </div>
                        </div>
                </div>
        </div>
</template>

<script>
import { defineComponent, ref, computed, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import ApplicationService from 'services/ApplicationService';
import { showDeleteConfirmation } from 'utils/swal';
import Swal from 'sweetalert2';

const showToast = (message, icon = 'success') => {
        Swal.fire({
                toast: true,
                position: 'top-end',
                icon,
                title: message,
                showConfirmButton: false,
                timer: 3000,
                timerProgressBar: true,
                customClass: {
                        popup: 'windmill-swal-toast',
                },
        });
};

export default defineComponent({
        name: 'ApiPermissionsPage',
        setup() {
                const router = useRouter();
                const applications = ref([]);
                const loading = ref(false);
                const saving = ref(false);
                const showCreateModal = ref(false);
                const newAppPartner = ref('');
                const newAppIpaddress = ref('');
                const searchQuery = ref('');
                const currentPage = ref(1);
                const pageSize = ref(10);

                const filteredApplications = computed(() => {
                        if (!searchQuery.value) return applications.value;
                        const query = searchQuery.value.toLowerCase();
                        return applications.value.filter(app =>
                                (app.partner && app.partner.toLowerCase().includes(query)) ||
                                (app.ipaddress && app.ipaddress.toLowerCase().includes(query))
                        );
                });

                const totalRows = computed(() => filteredApplications.value.length);
                const totalPages = computed(() => Math.ceil(totalRows.value / pageSize.value) || 1);

                const paginatedApplications = computed(() => {
                        const start = (currentPage.value - 1) * pageSize.value;
                        return filteredApplications.value.slice(start, start + pageSize.value);
                });

                const startRow = computed(() => {
                        if (totalRows.value === 0) return 0;
                        return (currentPage.value - 1) * pageSize.value + 1;
                });

                const endRow = computed(() => {
                        return Math.min(currentPage.value * pageSize.value, totalRows.value);
                });

                const fetchData = async () => {
                        loading.value = true;
                        try {
                                const params = new URLSearchParams({ limit: '0' });
                                applications.value = await ApplicationService.getAll(params);
                        } catch (error) {
                                showToast(error.message || 'Failed to load applications', 'error');
                        } finally {
                                loading.value = false;
                        }
                };

                const refreshData = () => {
                        fetchData();
                };

                const openCreateModal = () => {
                        newAppPartner.value = '';
                        newAppIpaddress.value = '';
                        showCreateModal.value = true;
                };

                const closeCreateModal = () => {
                        showCreateModal.value = false;
                };

                const createApplication = async () => {
                        if (!newAppPartner.value.trim()) return;
                        saving.value = true;
                        try {
                                const newApp = await ApplicationService.create({
                                        partner: newAppPartner.value.trim(),
                                        ipaddress: newAppIpaddress.value.trim() || null,
                                });
                                applications.value.unshift(newApp);
                                closeCreateModal();
                                showToast('Application created successfully');
                        } catch (error) {
                                showToast(error.message || 'Failed to create application', 'error');
                        } finally {
                                saving.value = false;
                        }
                };

                const editApplication = (app) => {
                        router.push({ name: 'EditApiPermission', params: { id: app.id } });
                };

                const confirmDeleteApplication = async (app) => {
                        const result = await showDeleteConfirmation(
                                'Delete Application',
                                `Are you sure you want to delete "${app.partner}"? This action cannot be undone.`
                        );
                        if (result.isConfirmed) {
                                try {
                                        await ApplicationService.delete(app.id);
                                        applications.value = applications.value.filter(a => a.id !== app.id);
                                        showToast('Application deleted successfully');
                                } catch (error) {
                                        showToast(error.message || 'Failed to delete application', 'error');
                                }
                        }
                };

                const goToPage = (page) => {
                        if (page >= 1 && page <= totalPages.value) {
                                currentPage.value = page;
                        }
                };

                onMounted(() => {
                        fetchData();
                });

                return {
                        applications,
                        loading,
                        saving,
                        showCreateModal,
                        newAppPartner,
                        newAppIpaddress,
                        searchQuery,
                        currentPage,
                        pageSize,
                        filteredApplications,
                        paginatedApplications,
                        totalRows,
                        totalPages,
                        startRow,
                        endRow,
                        refreshData,
                        openCreateModal,
                        closeCreateModal,
                        createApplication,
                        editApplication,
                        confirmDeleteApplication,
                        goToPage,
                };
        },
});
</script>

<style scoped>
@keyframes spin {
        from { transform: rotate(0deg); }
        to { transform: rotate(360deg); }
}
</style>
